import { ArrowRight, CalendarClock, CalendarRange, History } from "lucide-react";
import type { JobListSection } from "./JobListData";

const SECTION_COPY: Record<JobListSection, { title: string; text: string; icon: typeof CalendarClock }> = {
	onGoing: {
		title: "Inga pågående uppdrag",
		text: "Du har inga uppdrag som pågår just nu.",
		icon: CalendarClock,
	},
	upComing: {
		title: "Inga kommande uppdrag",
		text: "Du har inga uppdrag inplanerade framåt.",
		icon: CalendarRange,
	},
	past: {
		title: "Inga historiska uppdrag",
		text: "Här visas uppdrag som har avslutats.",
		icon: History,
	},
};

const SECTION_LINKS: { key: JobListSection; label: string }[] = [
	{ key: "onGoing", label: "Visa pågående uppdrag" },
	{ key: "upComing", label: "Visa kommande uppdrag" },
	{ key: "past", label: "Visa historiska uppdrag" },
];

interface Props {
	section: JobListSection;
	countBySection: Record<JobListSection, number>;
	/** A status or unit filter is active, so other jobs may be hidden */
	isFiltered: boolean;
	onChangeSection: (section: JobListSection) => void;
}

/**
 * Shown when the selected section has no jobs. Points to the other sections
 * that do have jobs, so the user isn't left wondering where their jobs went.
 */
const JobListEmptyState = ({ section, countBySection, isFiltered, onChangeSection }: Props) => {
	const { title, text, icon: Icon } = SECTION_COPY[section];
	const otherSections = SECTION_LINKS.filter(({ key }) => key !== section && countBySection[key] > 0);

	return (
		<div className="flex w-full flex-col items-center rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-12 text-center">
			<div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm">
				<Icon className="h-6 w-6 text-gray-500" />
			</div>
			<div className="text-lg font-semibold text-gray-900">{title}</div>
			<p className="!mt-1 !mb-0 max-w-md text-sm !text-gray-600">
				{text}
				{isFiltered && " Filtren du har valt kan dölja uppdrag."}
				{!otherSections.length && !isFiltered && " Skapa ett nytt uppdrag med knappen uppe till höger."}
			</p>
			{otherSections.length > 0 && (
				<div className="mt-6 flex flex-wrap justify-center gap-3">
					{otherSections.map(({ key, label }) => (
						<button
							key={key}
							type="button"
							onClick={() => onChangeSection(key)}
							className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-solid !border-gray-300 !bg-white !px-4 !py-2 !text-sm font-medium !text-gray-800 shadow-sm transition-colors hover:!bg-gray-100"
						>
							{label} ({countBySection[key]})
							<ArrowRight className="h-4 w-4" />
						</button>
					))}
				</div>
			)}
		</div>
	);
};

export default JobListEmptyState;
